import React, { useEffect, useState } from "react";
import ReactDOM from "react-dom/client";

import { AuthProvider, useAuth } from "./auth/AuthProvider";
import { licenseStore } from "./auth/license";
import { AdminPage } from "./components/AdminPage";
import { App } from "./components/App";
import { LicenseGate } from "./components/LicenseGate";
import "./styles/index.css";

// Three top-level routes, picked off the URL hash so we don't need
// react-router on the bundle:
//   #admin           — license-management page (password-protected
//                      via the API's ADMIN_PASSWORD secret).
//   anything else    — the sandbox, gated by the one-time license
//                      activation code stored in localStorage *or*
//                      a successful signin (existing accounts can
//                      bypass the gate from a fresh browser).
function Root() {
  const [hash, setHash] = useState(
    typeof window !== "undefined" ? window.location.hash : "",
  );

  useEffect(() => {
    const onHashChange = () => setHash(window.location.hash);
    window.addEventListener("hashchange", onHashChange);
    return () => window.removeEventListener("hashchange", onHashChange);
  }, []);

  if (hash === "#admin") {
    return <AdminPage />;
  }

  // AuthProvider wraps both the gate and the sandbox so a user can
  // sign in from inside the gate ("I already have an account").
  // After signin, the AuthProvider's signin handler rehydrates the
  // license sentinel into localStorage, so the next reload skips
  // the gate entirely.
  return (
    <AuthProvider>
      <GatedApp />
    </AuthProvider>
  );
}

function GatedApp() {
  // Two paths to "unlocked":
  //   1. localStorage has a license code (gate already passed
  //      on this browser, or rehydrated from a recent signin).
  //   2. The user is currently signed in — covers a fresh
  //      browser where /me succeeded against a stored token, or
  //      where the user just signed in via the gate's signin tab.
  const { user, loading } = useAuth();
  const [licenseUnlocked, setLicenseUnlocked] = useState<boolean>(
    () => licenseStore.get() !== null,
  );

  // While the AuthProvider is doing its initial /me probe a stale
  // token might still be valid, in which case the user shouldn't
  // see the gate flash up before disappearing. Holding off the
  // gate render until `loading` resolves prevents that flicker.
  if (loading) {
    return (
      <div className="license-gate" aria-busy="true">
        <div className="license-card" style={{ textAlign: "center" }}>
          <p style={{ color: "#8a8fa8", margin: 0 }}>Loading…</p>
        </div>
      </div>
    );
  }

  if (licenseUnlocked || user !== null) {
    return <App />;
  }

  return <LicenseGate onUnlock={() => setLicenseUnlocked(true)} />;
}

ReactDOM.createRoot(document.getElementById("root")!).render(
  <React.StrictMode>
    <Root />
  </React.StrictMode>,
);
